import { useMemo } from 'react';
import { Box, Paper, Typography, Tooltip, Button } from '@mui/material';
import {
  HealthAndSafetyOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
  ErrorOutlined,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { QuickAction } from '../../types/dashboard.types';
import { Employee } from '../../types/employee.types';
import { Shop } from '../../types/shop.types';
import { Schedule } from '../../types/schedule.types';
import { calculateScheduleHealth } from '../../utils/dashboardCalculations';

type ViewMode = 'daily' | 'weekly';

interface ScheduleHealthCardProps {
  rawData: {
    employees: Employee[];
    shops: Shop[];
    schedules: Schedule[];
  };
  viewMode: ViewMode;
  dateRange: string[];
  availabilityMode: "daily" | "weekly";
  unfilledShiftsAction?: QuickAction;
}

export default function ScheduleHealthCard({
  rawData,
  viewMode,
  dateRange,
  availabilityMode,
  unfilledShiftsAction,
}: ScheduleHealthCardProps) {
  const navigate = useNavigate();

  // Calculate health based on selected date range
  const data = useMemo(
    () => calculateScheduleHealth(rawData.employees, rawData.shops, rawData.schedules, dateRange),
    [rawData.employees, rawData.shops, rawData.schedules, dateRange]
  );

  // In weekly availability mode, over-scheduled warnings are not applicable
  const showOverScheduled = availabilityMode === "daily" && data.overScheduledWarnings.length > 0;

  // Recalculate total issues based on availability mode
  const effectiveTotalIssues = availabilityMode === "weekly"
    ? data.unfilledShifts.length
    : data.totalIssues;

  const periodLabel = viewMode === 'daily' ? 'today' : 'this week';

  const getStatusConfig = () => {
    if (effectiveTotalIssues === 0) {
      return {
        icon: <CheckCircleOutlined />,
        color: 'success' as const,
        label: 'Healthy',
        bgColor: 'success.50',
      };
    } else if (effectiveTotalIssues <= 3) {
      return {
        icon: <WarningAmberOutlined />,
        color: 'warning' as const,
        label: 'Needs Attention',
        bgColor: 'warning.50',
      };
    } else {
      return {
        icon: <ErrorOutlined />,
        color: 'error' as const,
        label: 'Critical',
        bgColor: 'error.50',
      };
    }
  };

  const status = getStatusConfig();

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: 'primary.light',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <HealthAndSafetyOutlined color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Schedule Health
        </Typography>
      </Box>

      {/* Status Banner */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          mb: 2,
          borderRadius: 2,
          bgcolor: status.bgColor,
        }}
      >
        <Box sx={{ color: `${status.color}.main` }}>{status.icon}</Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} color={`${status.color}.main`}>
            {status.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {effectiveTotalIssues === 0
              ? 'All schedules look good!'
              : `${effectiveTotalIssues} issue${effectiveTotalIssues > 1 ? 's' : ''} found ${periodLabel}`}
          </Typography>
        </Box>
      </Box>

      {/* Issues List */}
      {effectiveTotalIssues > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Unfilled Shifts */}
          {data.unfilledShifts.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Tooltip title="Shifts that need more staff coverage" placement="top">
                  <Typography variant="body2" color="text.secondary">
                    Unfilled Shifts ({data.unfilledShifts.length})
                  </Typography>
                </Tooltip>
                {unfilledShiftsAction && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                    onClick={() => navigate(unfilledShiftsAction.actionPath)}
                    sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                  >
                    Fix
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 150, overflow: 'auto' }}>
                {data.unfilledShifts.map((shift, index) => (
                  <Tooltip
                    key={`${shift.shopId}-${shift.date}-${index}`}
                    title={`${shift.filled} of ${shift.required} positions filled`}
                    placement="top"
                  >
                    <Paper
                      sx={{
                        p: 1,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'error.200',
                        bgcolor: 'error.50',
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: shift.shopColor,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8rem' }}>
                          {shift.shopName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {viewMode === 'weekly' ? `${shift.displayDate} • ` : ''}{shift.timeSlot} • +{shift.shortage}
                        </Typography>
                      </Box>
                    </Paper>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          {/* Over-scheduled (only in daily availability mode) */}
          {showOverScheduled && (
            <Box>
              <Tooltip title="Employees scheduled more than their available hours" placement="top">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Over-scheduled ({data.overScheduledWarnings.length})
                </Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 150, overflow: 'auto' }}>
                {data.overScheduledWarnings.map((warning, index) => (
                  <Tooltip
                    key={`${warning.employeeId}-${warning.date}-${index}`}
                    title={`Scheduled ${warning.excessHours}h more than available`}
                    placement="top"
                  >
                    <Paper
                      sx={{
                        p: 1,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'warning.200',
                        bgcolor: 'warning.50',
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: 'warning.main',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8rem' }}>
                          {warning.employeeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {viewMode === 'weekly' ? `${warning.displayDate} • ` : ''}{warning.scheduledHours}h / {warning.availableHours}h
                        </Typography>
                      </Box>
                    </Paper>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Empty state */}
      {effectiveTotalIssues === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
          }}
        >
          <CheckCircleOutlined sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No scheduling issues {periodLabel}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
